// Resolves the SOL-per-share value for every enabled Drip vault in one pass.
//
// The fallback of 1 SOL/share exists to keep depositor rankings and
// vaultSharePct proportions sane before vault state has loaded. That fallback
// is only safe when it is applied uniformly across every vault: scaling every
// position by the same constant preserves ranking and proportions, but
// scaling only some positions by it does not. So this is all-or-nothing:
// if any enabled vault cannot produce a usable per-share value, every vault
// falls back to 1, not just the broken one.

export interface DripSolPerShareInput {
  vaultId: string;
  /** SOL value backing the vault's shares (DripVaultState.stakedSol), or null if unavailable. */
  stakedSol: number | null;
  /** Share mint supply (DripVaultState.shareSupply), or null if unavailable. */
  shareSupply: number | null;
}

// A zero stakedSol is as unusable as a missing one: it yields solPerShare 0,
// and a per-vault 0 puts that vault's positions back on a different scale from
// every other vault's — exactly the unit mixing the all-or-nothing rule exists
// to prevent.
const isUsable = (input: DripSolPerShareInput): boolean =>
  input.stakedSol != null &&
  input.stakedSol > 0 &&
  input.shareSupply != null &&
  input.shareSupply > 0;

/**
 * Returns one SOL-per-share value per input, aligned by array position.
 *
 * If every vault is usable, each gets `stakedSol / shareSupply`. If any
 * vault is unusable (missing or zero staked value, missing share supply, or a
 * zero share supply), every vault gets `1` instead, restoring the
 * uniform-scaling assumption the fallback relies on.
 */
export const resolveSolPerShare = (inputs: DripSolPerShareInput[]): number[] => {
  const unusable = inputs.find((input) => !isUsable(input));
  if (unusable) {
    console.warn(
      `Drip: vault "${unusable.vaultId}" has no usable SOL-per-share value; ` +
        "falling back to 1 SOL/share for all vaults so depositor rankings stay comparable."
    );
    return inputs.map(() => 1);
  }

  return inputs.map(
    (input) => (input.stakedSol as number) / (input.shareSupply as number)
  );
};
